import type { UserPreferences } from '@/components/onboarding/types';
import type { Language } from '@/i18n/translations';
import { USECASE_EXAMPLE_PROMPTS } from './examplePrompts';
import {
  EXPERIENCE_DESCRIPTORS,
  ROLE_DESCRIPTORS,
  USECASE_DESCRIPTORS,
} from './descriptors';

/**
 * First-message shown in AiAssistantDialog when conversation is empty.
 * Renders as an assistant-authored message (not from the LLM).
 */
export function buildWelcomeMessage(
  prefs: UserPreferences | null,
  lang: Language,
): string {
  if (!prefs) {
    return lang === 'sv'
      ? 'Hej! Jag är Flow Weavers AI-assistent. Logga in och svara på några frågor i profilmenyn så anpassar jag mig efter dig.'
      : 'Hi! I\'m the Flow Weaver AI assistant. Sign in and answer a few profile questions to tailor me to you.';
  }

  const roleDesc = ROLE_DESCRIPTORS[prefs.role] ?? ROLE_DESCRIPTORS.other;
  const expDesc = EXPERIENCE_DESCRIPTORS[prefs.experience] ?? EXPERIENCE_DESCRIPTORS.beginner;
  const useCaseDesc = USECASE_DESCRIPTORS[prefs.useCase] ?? USECASE_DESCRIPTORS.sandbox;
  const role = roleDesc[lang] ?? roleDesc.en;
  const exp = expDesc[lang] ?? expDesc.en;
  const useCase = useCaseDesc[lang] ?? useCaseDesc.en;

  const personaLine = lang === 'sv'
    ? `Jag är anpassad efter din profil: ${role}, ${exp}, bygger ${useCase}. Du kan justera detta via profil-menyn — det ändrar hur jag pratar med dig.`
    : `I'm tailored to your profile: ${role}, ${exp}, building ${useCase}. You can adjust this via the profile menu — it changes how I talk to you.`;

  const exampleSet = USECASE_EXAMPLE_PROMPTS[prefs.useCase] ?? USECASE_EXAMPLE_PROMPTS.sandbox;
  const examples = exampleSet[lang] ?? exampleSet.en;
  const examplesHeader = lang === 'sv' ? 'Exempel-prompts:' : 'Example prompts:';
  const examplesList = examples.map(e => `- ${e}`).join('\n');

  return `${personaLine}\n\n${examplesHeader}\n${examplesList}`;
}

/**
 * System-notice text shown when user updates profile mid-conversation.
 */
export function buildPrefsChangeNotice(
  prefs: UserPreferences,
  lang: Language,
): string {
  const roleDesc = ROLE_DESCRIPTORS[prefs.role] ?? ROLE_DESCRIPTORS.other;
  const useCaseDesc = USECASE_DESCRIPTORS[prefs.useCase] ?? USECASE_DESCRIPTORS.sandbox;
  const role = roleDesc[lang] ?? roleDesc.en;
  const useCase = useCaseDesc[lang] ?? useCaseDesc.en;
  return lang === 'sv'
    ? `🔄 Din profil uppdaterades (${role}, ${useCase}). AI:ns beteende har justerats för nästa svar.`
    : `🔄 Your profile updated (${role}, ${useCase}). AI behavior has been adjusted for the next reply.`;
}
